import { CropFree as CropFreeIcon } from "@mui/icons-material";
import {
  Autocomplete,
  CircularProgress,
  FilterOptionsState,
  FormHelperText,
  Grid,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { BarcodeScannerDialog } from "components/barcodeScan";
import { ScannerMachineSuggestDialog } from "components/barcodeScan/ScannerMachineSuggestDialog";
import StyledTextField from "components/StyledTextField";
import { scanditLicense } from "env";
import { addressToString } from "helpers";
import { filterMachineList } from "helpers/filterMachineList";
import { useDebounce } from "hooks/useDebounce";
import { MachineType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { getMachines } from "store/slices/autocompletes/machine.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

const StyledDetails = styled("div")(() => ({
  marginTop: 5,
  marginLeft: 14,
}));

export interface MachineAutocompleteProps {
  customerId: string | undefined;
  error?: string;
  onChange: (machine: MachineType | null) => void;
}

export const MachineAutocomplete: FC<MachineAutocompleteProps> = (props) => {
  const { customerId, error, onChange } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { machines, machinesLoading } = useAppSelector((s) => s.machineAutocomplete);
  const [machineACOpen, setMachineACOpen] = useState(false);
  const [machineInputValue, setMachineInputValue] = useState<string>("");
  const [machineSearch, setMachineSearch] = useState<string>("");
  const [selectedMachine, setSelectedMachine] = useState<MachineType | null>(null);
  const debouncedMachineSearch = useDebounce<string>(machineSearch, 500);
  const [openScanner, setOpenScanner] = useState(false);
  const [openSuggested, setOpenSuggested] = useState(false);
  const [possibleMatches, setPossibleMatches] = useState<MachineType[]>([]);
  const [scannedValue, setScannedValue] = useState<string | null>("");
  const hasBarcodeScanner = scanditLicense();

  useEffect(() => {
    dispatch(
      getMachines({
        machineNo: debouncedMachineSearch,
        customerId: customerId,
      }),
    );
  }, [debouncedMachineSearch, customerId, dispatch]);

  const onSelectMachine = useCallback(
    (machine: MachineType | null) => {
      setSelectedMachine(machine);
      onChange(machine);
    },
    [onChange],
  );

  const onScan = useCallback(
    (scannerValue: string) => {
      setScannedValue(scannerValue);
      setOpenScanner(false);
      setMachineInputValue(scannerValue);

      dispatch(
        getMachines({
          machineNo: scannerValue,
          customerId: customerId,
        }),
      )
        .then(unwrapResult)
        .then((machines) => {
          const testValue = scannerValue.toLowerCase();
          let scanMatches: MachineType[] = [];
          let equalMatch: MachineType | null = null;
          for (const machine of machines) {
            const machineNo = machine.machineNo?.toLowerCase() ?? "";

            if (machineNo === testValue) {
              equalMatch = machine;
              break;
            } else if (machineNo.includes(testValue)) {
              scanMatches.push(machine);
            }
          }

          if (equalMatch !== null) {
            onSelectMachine(equalMatch);
          } else {
            if (scanMatches.length === 0) {
              dispatch(addSnackbarMessage({ key: "No-barcode-match", argument: scannerValue }));
            } else {
              setPossibleMatches(scanMatches);
              setOpenSuggested(true);
            }
          }
        });
    },
    [customerId, dispatch, onSelectMachine],
  );

  const handleCloseSuggest = useCallback(() => {
    setOpenSuggested(false);
    setScannedValue(null);
  }, []);

  return (
    <>
      <Grid item width="100%">
        <Autocomplete
          open={machineACOpen}
          onOpen={() => setMachineACOpen(true)}
          onClose={() => setMachineACOpen(false)}
          value={selectedMachine}
          onChange={(_, value, reason) => {
            if (reason === "clear") {
              onSelectMachine(null);
              setMachineSearch("");
              setMachineInputValue("");
            } else {
              if (value) {
                onSelectMachine(value);
              }
            }
          }}
          getOptionLabel={(o) => `${o.machineNo}`}
          renderOption={(props, option) => {
            return (
              <li {...props} key={`${option.id}-${option.name}`} style={{ display: "block" }}>
                <Typography variant="body1">{option.machineNo}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {option.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {option.idNo}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {option.location}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {addressToString(option.address)}
                </Typography>
              </li>
            );
          }}
          inputValue={machineInputValue}
          onInputChange={(_, value) => {
            setMachineSearch(value);
            setMachineInputValue(value);
          }}
          isOptionEqualToValue={(option, value) =>
            value && value.id ? option.id === value.id : true
          }
          getOptionDisabled={(option) => option.id === selectedMachine?.id}
          filterOptions={(options: MachineType[], state: FilterOptionsState<MachineType>) => {
            return filterMachineList(options, state.inputValue);
          }}
          options={machines}
          loading={machinesLoading}
          renderInput={(params) => (
            <StyledTextField
              name="machine"
              error={!!error}
              {...params}
              label={intl.formatMessage({
                id: "job.machine.machineNo",
              })}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {machinesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {!!selectedMachine?.id && (
          <StyledDetails>
            {!!selectedMachine.location && (
              <Typography variant="body2" color="textSecondary">
                {selectedMachine.location}
              </Typography>
            )}
            {!!selectedMachine.name && (
              <Typography variant="body2" color="textSecondary">
                {selectedMachine.name}
              </Typography>
            )}
            {!!selectedMachine.idNo && (
              <Typography variant="body2" color="textSecondary">
                {selectedMachine.idNo}
              </Typography>
            )}
            {!!selectedMachine.address && (
              <Typography variant="body2" color="textSecondary">
                {addressToString(selectedMachine.address)}
              </Typography>
            )}
          </StyledDetails>
        )}
        <FormHelperText error>{error}</FormHelperText>
      </Grid>

      {hasBarcodeScanner && (
        <>
          <Grid item>
            <IconButton
              data-testid="MachineScannerHandler-BarcodeButton"
              onClick={() => setOpenScanner(true)}
              size="large"
              disabled={machinesLoading}
              sx={{ pr: 0, ml: 1 }}
            >
              <CropFreeIcon fontSize="large" />
            </IconButton>
          </Grid>

          <BarcodeScannerDialog
            open={openScanner}
            setOpen={(open: boolean) => setOpenScanner(open)}
            submitValue={onScan}
          />

          <ScannerMachineSuggestDialog
            open={openSuggested}
            scannerValue={scannedValue}
            machines={possibleMatches}
            selectMachine={onSelectMachine}
            onClose={handleCloseSuggest}
          />
        </>
      )}
    </>
  );
};
